import { ErrorView } from "@/components/ui/layout/error/errorView"
import { CoachOfferPricingOptionForm } from "@/modules/coachOffers/components/coachOfferPricingOptionForm"
import type { CoachOfferPricingFormValues } from "@/modules/coachOffers/types/coachOfferFormTypes"
import { CoachOfferCreationForm } from "@modules/coachOffers/components/coachOfferCreationForm"
import { CoachOfferOrderForm } from "@modules/coachOffers/components/coachOfferOrderForm"
import { CoachOfferRemoteTypeSelectionView } from "@modules/coachOffers/components/coachOfferRemoteTypeSelectionView"
import { CoachOfferResumeView } from "@modules/coachOffers/components/coachOfferResumeView"
import type { CoachOfferEntity } from "@modules/coachOffers/entities/coachOfferEntity"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { LoadingSpinner } from "@modules/ui/components/loading"
import { Suspense } from "react"
import { ErrorBoundary } from "react-error-boundary"

export function useCoachOfferSidebar() {
  const { sidebarNavigateTo, addSidebarStackOnTop } = useSidebar()

  const navigateToCoachOfferCreation = () =>
    sidebarNavigateTo({
      content: (
        <ErrorBoundary fallback={<ErrorView />}>
          <Suspense fallback={<LoadingSpinner />}>
            <CoachOfferCreationForm />
          </Suspense>
        </ErrorBoundary>
      ),
      replaceAllComponents: true,
    })

  const navigateToRemoteOfferTypeSelection = () =>
    sidebarNavigateTo({
      content: (
        <ErrorBoundary fallback={<ErrorView />}>
          <Suspense fallback={<LoadingSpinner />}>
            <CoachOfferRemoteTypeSelectionView />
          </Suspense>
        </ErrorBoundary>
      ),
      replaceAllComponents: true,
    })

  const navigateToCoachOfferEdit = (offer: CoachOfferEntity) =>
    sidebarNavigateTo({
      content: (
        <ErrorBoundary fallback={<ErrorView />}>
          <Suspense fallback={<LoadingSpinner />}>
            <CoachOfferCreationForm initialOffer={offer} />
          </Suspense>
        </ErrorBoundary>
      ),
    })

  const navigateToCoachOfferResume = (offerId: string) =>
    sidebarNavigateTo({
      content: (
        <ErrorBoundary fallback={<ErrorView />}>
          <Suspense fallback={<LoadingSpinner />}>
            <CoachOfferResumeView offerId={offerId} onEdit={(offer) => void navigateToCoachOfferEdit(offer)} />
          </Suspense>
        </ErrorBoundary>
      ),
    })

  const navigateToCoachOfferOrder = () =>
    sidebarNavigateTo({
      content: (
        <ErrorBoundary fallback={<ErrorView />}>
          <Suspense fallback={<LoadingSpinner />}>
            <CoachOfferOrderForm />
          </Suspense>
        </ErrorBoundary>
      ),
      replaceAllComponents: true,
    })

  const navigateToCoachOfferPriceEdit = (
    defaultValues: CoachOfferPricingFormValues,
    onValidSubmit: (values: CoachOfferPricingFormValues) => void,
  ) => {
    addSidebarStackOnTop({
      content: <CoachOfferPricingOptionForm defaultValues={defaultValues} onValidSubmit={onValidSubmit} />,
    })
  }

  return {
    navigateToCoachOfferCreation,
    navigateToCoachOfferEdit,
    navigateToCoachOfferResume,
    navigateToCoachOfferOrder,
    navigateToRemoteOfferTypeSelection,
    navigateToCoachOfferPriceEdit,
  }
}
